import type { Identifier } from "ra-core";

/**
 * Almikia Epic EG (TASK-043): expense row types. Column names mirror the
 * TASK-042 schema (`expenses`, `expense_categories`, the `expenses_summary` and
 * `expense_monthly_totals` views) verbatim. Money fields are numbers holding
 * 2-decimal MXN values; they are only ever rounded by `roundMoney` in
 * `expenseRules.ts`.
 */

export const EXPENSE_PAID_BY_VALUES = ["sibaris", "pepe_diaz", "otro"] as const;
export type ExpensePaidBy = (typeof EXPENSE_PAID_BY_VALUES)[number];

export const EXPENSE_PAYMENT_METHODS = [
  "efectivo",
  "transferencia",
  "tarjeta",
  "otro",
] as const;
export type ExpensePaymentMethod = (typeof EXPENSE_PAYMENT_METHODS)[number];

/** `expense_categories`: archived (`archived_at`), never deleted. */
export type ExpenseCategory = {
  id: Identifier;
  name: string;
  archived_at: string | null;
  created_at: string;
  created_by: Identifier | null;
  updated_at: string;
  updated_by: Identifier | null;
};

/** `expenses_summary`: the expense row plus the joined and computed fields. */
export type Expense = {
  id: Identifier;
  expense_date: string;
  concept: string;
  category_id: Identifier;
  supplier: string | null;
  amount_mxn: number;
  has_iva: boolean;
  iva_rate: number;
  /** Generated by the database (amount is IVA-inclusive); never written. */
  iva_amount: number;
  is_invoiced: boolean;
  cfdi_uuid: string | null;
  cfdi_pdf_path: string | null;
  cfdi_xml_path: string | null;
  paid_by: ExpensePaidBy;
  paid_by_other: string | null;
  payment_method: ExpensePaymentMethod;
  project_id: Identifier | null;
  notes: string | null;
  receipt_path: string | null;
  reimbursed: boolean;
  reimbursed_at: string | null;
  cancelled_at: string | null;
  cancelled_by: Identifier | null;
  cancel_reason: string | null;
  created_at: string;
  created_by: Identifier | null;
  updated_at: string;
  updated_by: Identifier | null;
  // View-only fields (`expenses_summary`).
  category_name: string;
  project_name: string | null;
  project_code: string | null;
  subtotal_mxn: number;
  is_cancelled: boolean;
  is_pending_reimbursement: boolean;
};

/** The `expenses` table row: an {@link Expense} without the view-only fields. */
export type ExpenseRow = Omit<
  Expense,
  | "category_name"
  | "project_name"
  | "project_code"
  | "subtotal_mxn"
  | "is_cancelled"
  | "is_pending_reimbursement"
>;

/** `expense_monthly_totals`: per month, category and payer; cancelled rows excluded. */
export type ExpenseMonthlyTotal = {
  /** First day of the month, `YYYY-MM-DD`. */
  month: string;
  category_id: Identifier;
  category_name: string;
  paid_by: ExpensePaidBy;
  expense_count: number;
  total_amount_mxn: number;
  total_iva_mxn: number;
};

/** The only categories of file an expense carries (private `expense-documents` bucket). */
export type ExpenseFileKind = "receipt" | "cfdi_pdf" | "cfdi_xml";

/** What an upload returns: the object path to store on the matching expense column. */
export type ExpenseFileMetadata = {
  path: string;
  file_name: string;
  mime_type: string;
};
